import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import { InfoOutlined } from '@mui/icons-material';
import { Backdrop, Box, Card, CircularProgress, Container, Fab, Skeleton, Typography, useTheme } from '@mui/material';

import { CartWebSocketsEvents } from '@bandido/shared/types';

import { useGetHotelCardData, useGetHotelRating } from '@frontend/features/hotel-content';
import {
  HotelFaresViewDetail,
  HotelFaresViewImageExplorer,
  parseFareDetailParams,
} from '@frontend/features/hotel/components/Fares';
import { HotelFaresList } from '@frontend/features/hotel/components/HotelFaresList';
import { useCart } from '@frontend/features/cart/components/CartContext';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useHotelSearch } from '@frontend/features/hotel/components/Availability/Search/HotelSearchContext';
import { QueryKeys } from '@frontend/features/common/types';
import { HotelFaresFilters } from '@frontend/features/hotel/components/HotelFaresFilters';
import { HotelFareFinalDto, SaleOrderItemDto } from '@bandido/shared/dtos';
import { CONFIG } from '@frontend/config';
import { useWS } from '@frontend/features/common/hooks/useWS';

import { routes } from '../routes';


export const HotelFares: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { socketRef, joinRoom, leaveRoom } = useWS(`${CONFIG.WS_URL}/cart`);
  const { addToast } = useSnackBar();
  const { addItem, state, createOrderIsLoading } = useCart();
  const [quotationStatus, setQuotationStatus] = useState({
    inProgress: false,
    fareId: 0,
  });
  const { propertyFares, toggleShowPropertyFaresScope, searchId, filtersPropertyFares } = useHotelSearch();
  const [searchParams] = useSearchParams();
  const doSearch = searchParams.get('doSearch') === 'true';
  const propertyId = searchParams.get('propertyId');
  const queryClient = useQueryClient();


  const { hotelId } = parseFareDetailParams(searchParams);

  const { data: hotel, isLoading: loadingHotel } = useGetHotelCardData({
    hotelId: (doSearch && propertyId) ? propertyId : hotelId
  });

  const { data: hotelRating, isLoading: loadingHotelRating } = useGetHotelRating({
    hotelId: (doSearch && propertyId) ? propertyId : hotelId,
  });

  const handleContinueToCart = async (fare: HotelFareFinalDto) => {
    try {
      if (fare) {
        setQuotationStatus({
          inProgress: true,
          fareId: fare.id,
        });
        await addItem({
          item: {
            type: 'hotel',
            metadata: {
              ...fare,
              nimbuxHotelId: Number((doSearch && propertyId) ? propertyId : hotelId),
              occupancies: fare.occupancies,
            } as unknown as SaleOrderItemDto['metadata'],
            price: fare?.finalPrice,
            markupRuleId: fare?.markupId || null,
            markupIsFixed: fare?.markupIsFixed,
            markupValue: fare?.markupValue,
          },
          socketId: socketRef.current?.id || '',
        });
      }
    } catch (_e) {
      addToast({
        message: t('cart.convert.error'),
        color: 'error',
      });
    }
  };

  // Helper function to clean search term (remove "Nx " pattern)
  const cleanSearchTerm = (term: string): string => {
    // Remove pattern like "1x ", "2x ", "10x ", etc.
    return term.replace(/^\d+x\s+/i, '').trim();
  };

  const renderFaresList = useCallback(() => {
    if (doSearch) {
      if (propertyFares.length < 1) {
        return <Skeleton sx={{ minHeight: '100px' }} />;
      }

      const sortedPropertyFares = [...propertyFares]
        .filter(item => item.searchId === searchId)
        .filter(item => {
          const {
            board,
            accessCode,
            reembolsable,
            roomName,
            priceRange,
          } = filtersPropertyFares;

          const matchesBoard = board ? item.boardDescription === board : true;
          const matchesAccessCode = accessCode ? item.context === accessCode : true;
          const matchesRefundable = reembolsable ? item.refundable === true : true;
          const matchesPrice = priceRange
            ? item.finalPrice >= priceRange[0] && item.finalPrice <= priceRange[1]
            : true;

          const matchesRoomName = roomName
            ? item.rooms?.some((room) => {
              if (typeof room?.description !== 'string') return false;
              
              const cleanedSearchTerm = cleanSearchTerm(roomName);
              return room.description.toLowerCase().includes(cleanedSearchTerm.toLowerCase());
            })
            : true;

          return (
            matchesBoard &&
            matchesAccessCode &&
            matchesRefundable &&
            matchesPrice &&
            matchesRoomName
          );
        })
        .sort((a, b) => a.finalPrice - b.finalPrice);

      return (
        <HotelFaresList
          fares={sortedPropertyFares}
          onSelect={handleContinueToCart}
          quotationStatus={quotationStatus}
        />
      );
    }
    return null;
  }, [propertyFares, filtersPropertyFares, quotationStatus, searchId]);


  useEffect(() => {
    if (socketRef.current?.id && state?.id) {
      joinRoom(JSON.stringify({
        saleOrderId: state?.id,
      }));

      socketRef.current.on(CartWebSocketsEvents.QUOTATION_SUCESS, () => {
        try {
          navigate(routes.salesorder.cart);
        } catch (error) {
          addToast({
            color: 'error',
            message: t('cart.convert.error'),
          });
        } finally {

          queryClient.invalidateQueries([QueryKeys.saleOrder]);
          setQuotationStatus({
            inProgress: false,
            fareId: 0,
          });
        }
      });

      socketRef.current.on(CartWebSocketsEvents.QUOTATION_ERROR, (error: string) => {
        try {
          const { error: parsedError } = JSON.parse(error);
          addToast({
            message: parsedError.message || t('common.error.server'),
            color: 'error',
          });
        } catch (error) {
          addToast({
            message: t('common.error.server'),
            color: 'error',
          });
        } finally {
          setQuotationStatus({
            inProgress: false,
            fareId: 0,
          });
        }
      });

      return () => {
        socketRef.current?.off(CartWebSocketsEvents.BOOKING_SUCESS);
        socketRef.current?.off(CartWebSocketsEvents.BOOKING_ERROR);
        leaveRoom(JSON.stringify({
          saleOrderId: state?.id,
        }));
      };
    }
  }, [socketRef.current?.id, state?.id]);

  return (
    <Container sx={{ mt: 5 }}>
      {(loadingHotel || loadingHotelRating) &&
        (
          <>
            <Typography variant='body2'>{t('hotels.availability.loading')}</Typography>
            <CircularProgress />
          </>
        )
      }

      {
        hotel && (
          <>
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
            >
              <Box
                component={Card}
                elevation={2}
                display="flex"
                flexDirection="column"
                padding={theme.spacing(3)}
              >
                <HotelFaresViewImageExplorer images={hotel.medias} />
                <HotelFaresViewDetail
                  name={hotel.name}
                  categoryId={hotel.categoryId}
                  address={hotel.address}
                  city={hotel.city}
                  country={hotel.country}
                  amenities={hotel.amenities}
                  rating={hotelRating?.rating || 0}
                  totalReviews={hotelRating?.totalReviews || 0}
                />
              </Box>
              <HotelFaresFilters />
            </Box>
            <Box mt={'1px'}>
              {renderFaresList()}
            </Box>
          </>
        )
      }
      <Backdrop
        open={createOrderIsLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: theme.spacing(2),
          right: theme.spacing(2),
          zIndex: 1000,
        }}
        onClick={toggleShowPropertyFaresScope}
      >
        <InfoOutlined />
      </Fab>
    </Container>
  );
};
